import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { OrderStatus, OrderType } from "../../types";
import { useUpdateOrderStatusMutation } from "../../store";
import { IoChevronDown } from "react-icons/io5";
import { showToast, ToastType } from "../../utils/toast.utils";
import { orderStatusColors, OrderStatusLabels } from "../../configs";

interface OrderStatusSelectProps {
  orderId: string;
  value: OrderStatus;
  orderType: OrderType;
  className?: string;
}

const OrderStatusSelect: React.FC<OrderStatusSelectProps> = ({
  orderId,
  value,
  orderType,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  const deliveryStatuses = [
    OrderStatus.WAITING_FOR_PAYMENT,
    OrderStatus.PROCESSING,
    OrderStatus.COOKING,
    OrderStatus.WAITING_FOR_RIDER,
    OrderStatus.DRIVER_ASSIGNED,
    OrderStatus.COLLECTED,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELLED,
  ];

  const pickupStatuses = [
    OrderStatus.WAITING_FOR_PAYMENT,
    OrderStatus.PROCESSING,
    OrderStatus.COOKING,
    OrderStatus.WAITING_FOR_PICKUP,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELLED,
  ];

  const availableStatuses =
    orderType === OrderType.DELIVERY ? deliveryStatuses : pickupStatuses;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const handleSelect = async (newStatus: OrderStatus) => {
    if (newStatus === value) {
      setIsOpen(false);
      return;
    }
    setIsUpdating(true);
    const result = await updateOrderStatus({ orderId, status: newStatus });
    if (!result.error) {
      showToast("Statut mis à jour", ToastType.SUCCESS);
    }
    setIsUpdating(false);
    setIsOpen(false);
  };

  const wrapperClasses = classNames("relative inline-block text-left w-[180px]", className);

  // Modern badge styling
  const currentBgColor = orderStatusColors[value] || "bg-gray-100";
  const badgeClasses = twMerge(
    classNames(
      "flex justify-between items-center px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all shadow-sm w-full",
      currentBgColor,
      "text-white hover:opacity-90 active:scale-95",
      isUpdating ? "opacity-50 cursor-wait" : ""
    )
  );

  return (
    <div ref={containerRef} className={wrapperClasses}>
      {/* Trigger */}
      <div
        className={badgeClasses}
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        title="Changer le statut"
      >
        <span className="truncate mr-2 flex-1 text-center">{OrderStatusLabels[value]}</span>
        <IoChevronDown
          className={classNames("transition-transform duration-200 flex-shrink-0", {
            "rotate-180": isOpen,
          })}
          size={14}
        />
      </div>

      {/* Floating Menu */}
      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-full min-w-[180px] bg-white border border-gray-100 shadow-xl rounded-lg py-1 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100">
          {availableStatuses.map((key) => {
            const label = OrderStatusLabels[key as OrderStatus];
            const isSelected = key === value;
            const statusColor = orderStatusColors[key as OrderStatus];
            
            return (
              <div
                key={key}
                className={classNames(
                  "px-4 py-2 cursor-pointer text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors",
                  isSelected ? "bg-gray-50 font-medium" : "text-gray-600"
                )}
                onClick={() => handleSelect(key as OrderStatus)}
              >
                <span className={`w-2 h-2 rounded-full ${statusColor}`} />
                {label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderStatusSelect;
